import { formatLessonName } from '@/utils/lesson/name';
import i18n from '@/lib/i18n';
import { AlertTypeEnum } from '@/models/Alert';
import ScheduledLesson from '@/models/ScheduledLesson';
import moment from 'moment';

interface Message {
  createMessage(): string;
}

class MessageBuilder {
  private presentation: string = '';
  private body: string = '';
  private footer: string = '';
  private question: string = '';
  private link: string = '';

  withAppresentation({ sender }: { sender: string }): this {
    this.presentation = i18n.t('alerts.messages.presentation', {
      sender,
    });
    return this;
  }

  withLatenessBody({
    scheduledLesson,
    studentName,
  }: {
    scheduledLesson?: ScheduledLesson;
    studentName: string;
  }): this {
    if (!scheduledLesson) {
      this.body = i18n.t('alerts.messages.simpleBody', {
        studentName,
      });
      return this;
    }
    const lessonDate = moment(scheduledLesson.datetime).format('HH:mm');
    const lessonName = formatLessonName(
      scheduledLesson.lesson,
      scheduledLesson,
    );
    this.body = i18n.t('alerts.messages.latelessBody', {
      studentName,
      lessonName,
      lessonDate,
    });
    return this;
  }

  withAbsenceBody({
    scheduledLesson,
    studentName,
  }: {
    scheduledLesson?: ScheduledLesson;
    studentName: string;
  }): this {
    if (!scheduledLesson) return this;
    const dateFormat =
      i18n.language === 'pt_BR'
        ? 'DD [de] MMMM [às] HH:mm'
        : 'MMMM DD [at] HH:mm';
    const lessonDate = moment(scheduledLesson.datetime)
      .locale(i18n.language)
      .format(dateFormat);

    const lessonName = formatLessonName(
      scheduledLesson.lesson,
      scheduledLesson,
    );
    this.body = i18n.t('alerts.messages.absenceBody', {
      studentName,
      lessonName,
      lessonDate,
    });
    return this;
  }

  withNotDoneHomework({
    scheduledLesson,
    studentName,
  }: {
    scheduledLesson?: ScheduledLesson;
    studentName: string;
  }): this {
    if (!scheduledLesson) return this;
    const lessonName = formatLessonName(
      scheduledLesson.lesson,
      scheduledLesson,
    );
    this.body = i18n.t('alerts.messages.homeworkNotDoneBody', {
      studentName,
      lessonName,
    });
    return this;
  }

  withOccurrence({ studentName }: { studentName: string }): this {
    this.body = i18n.t('alerts.messages.occurrenceBody', {
      studentName,
    });
    return this;
  }

  withLink(link?: string) {
    if (link)
      this.link = i18n.t('alerts.messages.link', {
        link,
      });
    return this;
  }

  withQuestion(question?: string) {
    this.question = question || i18n.t('alerts.messages.question');
    return this;
  }

  withFooter(footer?: string): this {
    this.footer = footer || i18n.t('alerts.messages.footer');
    return this;
  }

  build(): string {
    return `${this.presentation}\n${this.body}\n${this.link}\n${this.question}\n${this.footer}`;
  }
}

class LatenessMessage implements Message {
  constructor(
    private sender: string,
    private recipient: string,
    private scheduledLesson?: ScheduledLesson,
  ) {}

  createMessage(): string {
    const builder = new MessageBuilder()
      .withAppresentation({ sender: this.sender })
      .withLatenessBody({
        scheduledLesson: this.scheduledLesson,
        studentName: this.recipient,
      })
      .withLink(this.scheduledLesson?.url)
      .withFooter();
    return builder.build();
  }
}

class AbsenceMessage implements Message {
  constructor(
    private sender: string,
    private recipient: string,
    private body?: ScheduledLesson,
  ) {}

  createMessage(): string {
    const builder = new MessageBuilder()
      .withAppresentation({ sender: this.sender })
      .withAbsenceBody({
        studentName: this.recipient,
        scheduledLesson: this.body,
      })
      .withFooter();
    return builder.build();
  }
}

class HomeworkNotDoneMessage implements Message {
  constructor(
    private sender: string,
    private recipient: string,
    private body?: ScheduledLesson,
  ) {}

  createMessage(): string {
    const builder = new MessageBuilder()
      .withAppresentation({ sender: this.sender })
      .withNotDoneHomework({
        studentName: this.recipient,
        scheduledLesson: this.body,
      })
      .withFooter();
    return builder.build();
  }
}

class OccurrenceMessage implements Message {
  constructor(private sender: string, private recipient: string) {}

  createMessage(): string {
    const builder = new MessageBuilder()
      .withAppresentation({ sender: this.sender })
      .withOccurrence({ studentName: this.recipient });
    return builder.build();
  }
}

type CreateLatelessMessageArgs = {
  type: AlertTypeEnum.LATENESS;
  body: ScheduledLesson;
};

type CreateAbsenceMessageArgs = {
  type: AlertTypeEnum.ABSENCE;
  body: ScheduledLesson;
};

type CreateHomeworkNotDoneMessageArgs = {
  type: AlertTypeEnum.HOMEWORK_NOT_DONE;
  body: ScheduledLesson;
};

type CreateOccurrenceMessageArgs = {
  type: AlertTypeEnum.OCCURRENCE;
  body?: ScheduledLesson;
};

type CreateMessageArgs = {
  type: AlertTypeEnum;
  sender: string;
  recipient: string;
} & (
  | CreateLatelessMessageArgs
  | CreateAbsenceMessageArgs
  | CreateHomeworkNotDoneMessageArgs
  | CreateOccurrenceMessageArgs
);

export const createMessage = ({
  type,
  sender,
  recipient,
  body,
}: CreateMessageArgs): string => {
  let message: Message;

  if (type === AlertTypeEnum.LATENESS) {
    message = new LatenessMessage(sender, recipient, body);
  } else if (type === AlertTypeEnum.ABSENCE) {
    message = new AbsenceMessage(sender, recipient, body);
  } else if (type === AlertTypeEnum.HOMEWORK_NOT_DONE) {
    message = new HomeworkNotDoneMessage(sender, recipient, body);
  } else if (type === AlertTypeEnum.OCCURRENCE) {
    message = new OccurrenceMessage(sender, recipient);
  } else {
    throw new Error('Invalid alert type');
  }

  return message.createMessage();
};
